// src/pages/LandingPage.js
//
// Public marketing landing page shown to logged-out visitors on the root
// domain (www.heberthoops.com) only -- organizer subdomains still go
// straight to <Login>, since a subdomain visitor almost always has a
// specific invite link and specific intent (join a pool), not "shopping
// around" intent. See App.js for the host-based routing decision.
//
// "Start Your Pool" routes into the existing, already-built
// /signup-organizer self-signup flow (email verification, duplicate-email
// handling already implemented server-side) -- this page adds zero new
// backend logic, it's purely a new front door onto flows that already
// exist. "Log In" routes back to the normal <Login> view.

import React from 'react';

const FEATURES = [
    {
        tag: 'Your name, not ours',
        title: 'Your Brand, Your Pool',
        body: "A pool site with your own name on every page your players see, and the messages you send your group are signed with your pool's name.",
    },
    {
        tag: 'Zero setup work',
        title: 'Built From a Ready Bracket',
        body: 'Sign up and a full bracket is ready instantly — invite real players and try the whole admin experience on a demo season, free.',
    },
    {
        tag: "One bad pick, not one bad season",
        title: 'Round-by-Round, Not All-or-Nothing',
        body: "Let players pick fresh each round instead of locking in a whole bracket up front — nobody's out of it by Thursday.",
    },
    {
        tag: 'Both brackets included',
        title: "Men's and Women's, Equally Built",
        body: "Clone this year's Men's or Women's bracket — same real, full-featured pool either way.",
    },
];

const STEPS = [
    { num: '01', title: 'Create Your Pool', body: 'Sign up and name your pool. Free access starts right away, no time limit.' },
    { num: '02', title: 'Get Your Bracket', body: 'A full 64-team bracket is already built into your account — nothing to set up.' },
    { num: '03', title: 'Invite Your Group', body: 'Share one link. Everyone signs up and submits their picks.' },
    { num: '04', title: 'Watch It Play Out', body: 'Enter results as games finish. The leaderboard updates automatically.' },
];

const PLANS = [
    { id: 'eval', name: 'Free Eval', price: '$0', per: '', bullets: ['1 pool, 3 players', 'Demo season', 'No time limit'], featured: false, fineprint: 'One free evaluation per account' },
    { id: 'tier1', name: 'Starter', price: '$29', per: '/yr', bullets: ['4 pools', 'Up to 50 players / pool', 'Your own branded pool site'], featured: true },
    { id: 'tier2', name: 'Growth', price: '$49', per: '/yr', bullets: ['10 pools', 'Up to 100 players / pool', 'Custom colors + branded site'], featured: false },
    { id: 'tier3', name: 'Unlimited', price: '$100', per: '/yr', bullets: ['Unlimited pools', 'Up to 250 players / pool', 'Custom colors + branded site'], featured: false },
];

function LandingPage({ onStartPool, onLogin }) {
    return (
        <div className="min-h-screen bg-gray-950 text-gray-100">
            {/* NAV */}
            <nav className="flex items-center justify-between px-6 py-5 border-b border-gray-800">
                <div className="flex items-center gap-2.5">
                    <img src="/favicon.svg" alt="" className="h-7 w-7 flex-shrink-0" />
                    <span className="text-xl font-black tracking-tight">
                        HEBERT<span className="text-orange-500">HOOPS</span>
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onLogin}
                        className="text-gray-400 hover:text-white text-sm font-bold uppercase tracking-wide px-2 py-2"
                    >
                        Log In
                    </button>
                    <button
                        onClick={onStartPool}
                        className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold uppercase tracking-wide px-5 py-2.5 rounded-lg"
                    >
                        Start Your Pool
                    </button>
                </div>
            </nav>

            {/* HERO */}
            <section className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center py-20 md:py-24">
                <div>
                    <span className="block text-orange-400 text-xs font-bold uppercase tracking-widest mb-4">
                        One bad pick shouldn't end your season
                    </span>
                    <h1 className="text-4xl md:text-5xl font-black leading-tight mb-6">
                        The bracket pool that keeps <span className="text-orange-500">everyone</span> playing past round one.
                    </h1>
                    <p className="text-gray-400 text-lg max-w-md mb-8">
                        Pick fresh each round instead of living or dying on one bad pick. Your own branded pool
                        site, built instantly the moment you sign up — no spreadsheet, no waiting.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={onStartPool}
                            className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide text-sm px-7 py-3.5 rounded-lg"
                        >
                            Start Your Pool — Free
                        </button>
                        <a
                            href="#how-it-works"
                            className="border border-gray-700 text-gray-200 hover:border-gray-500 font-bold uppercase tracking-wide text-sm px-6 py-3.5 rounded-lg"
                        >
                            See How It Works
                        </a>
                    </div>
                </div>

                {/* Signature bracket graphic */}
                <div className="hidden md:block relative h-72 overflow-hidden">
                    <div className="relative" style={{ width: 590, height: 260, transform: 'scale(0.85)', transformOrigin: 'top left' }}>
                        <div className="absolute bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-[11px] font-mono text-gray-400" style={{ top: 20, left: 0, width: 90 }}>Team A</div>
                        <div className="absolute bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-[11px] font-mono text-gray-400" style={{ top: 180, left: 0, width: 90 }}>Team B</div>

                        <div className="absolute border-t-2 border-gray-700" style={{ top: 37, left: 90, width: 15 }}></div>
                        <div className="absolute border-t-2 border-gray-700" style={{ top: 197, left: 90, width: 15 }}></div>
                        <div className="absolute border-r-2 border-gray-700" style={{ top: 37, left: 105, height: 160 }}></div>
                        <div className="absolute border-t-2 border-gray-700" style={{ top: 116, left: 105, width: 15 }}></div>

                        <div className="absolute bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-[11px] font-mono text-gray-400" style={{ top: 100, left: 120, width: 90 }}>Winner</div>

                        <div className="absolute border-t-2 border-gray-700" style={{ top: 116, left: 210, width: 15 }}></div>

                        <div className="absolute bg-orange-500 text-white rounded-md px-4 py-3 text-sm font-black uppercase" style={{ top: 100, left: 225, width: 140 }}>Your Champion</div>

                        <div className="absolute border-t-2 border-gray-700" style={{ top: 116, left: 365, width: 15 }}></div>

                        <div className="absolute bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-[11px] font-mono text-gray-400" style={{ top: 100, left: 380, width: 90 }}>Winner</div>

                        <div className="absolute border-r-2 border-gray-700" style={{ top: 37, left: 485, height: 160 }}></div>
                        <div className="absolute border-t-2 border-gray-700" style={{ top: 37, left: 485, width: 15 }}></div>
                        <div className="absolute border-t-2 border-gray-700" style={{ top: 197, left: 485, width: 15 }}></div>

                        <div className="absolute bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-[11px] font-mono text-gray-400" style={{ top: 20, left: 500, width: 90 }}>Team C</div>
                        <div className="absolute bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-[11px] font-mono text-gray-400" style={{ top: 180, left: 500, width: 90 }}>Team D</div>
                    </div>
                </div>
            </section>

            {/* FEATURES -- no numbering, four independent value props */}
            <section className="bg-gray-900 border-y border-gray-800 py-20 px-6">
                <div className="max-w-6xl mx-auto grid sm:grid-cols-2 gap-10">
                    {FEATURES.map(f => (
                        <div key={f.title}>
                            <span className="block text-orange-400 text-[11px] font-bold uppercase tracking-widest mb-3">
                                {f.tag}
                            </span>
                            <h3 className="text-xl font-black mb-2">{f.title}</h3>
                            <p className="text-gray-400 text-sm max-w-sm">{f.body}</p>
                        </div>
                    ))}
                </div>
                <p className="text-center text-gray-500 text-sm max-w-3xl mx-auto mt-11">
                    Choose one format for your pool: fresh picks each round, or the classic whole-bracket style.
                </p>
            </section>

            {/* HOW IT WORKS -- genuine sequence, numbering earned */}
            <section id="how-it-works" className="max-w-6xl mx-auto px-6 py-20">
                <h2 className="text-3xl font-black text-center mb-14">From sign-up to tip-off in minutes</h2>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">
                    {STEPS.map(s => (
                        <div key={s.num}>
                            <span className="block text-orange-500 text-xs font-mono font-bold mb-2">{s.num}</span>
                            <h4 className="font-black uppercase text-base mb-1.5">{s.title}</h4>
                            <p className="text-gray-400 text-sm">{s.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* PRICING */}
            <section className="bg-gray-900 border-t border-gray-800 py-20 px-6">
                <h2 className="text-3xl font-black text-center mb-3">Pick a plan that fits your pool</h2>
                <p className="text-center text-gray-400 mb-14">Start free, no time limit. Upgrade anytime before your tournament kicks off.</p>
                <div className="max-w-6xl mx-auto grid sm:grid-cols-2 md:grid-cols-4 gap-5">
                    {PLANS.map(p => (
                        <div
                            key={p.id}
                            className={
                                'rounded-xl p-6 border ' +
                                (p.featured
                                    ? 'border-orange-500 bg-orange-500/5'
                                    : 'border-gray-800 bg-gray-950')
                            }
                        >
                            <div className="font-black uppercase text-sm mb-1">{p.name}</div>
                            <div className="text-orange-400 font-mono font-bold text-2xl mb-4">
                                {p.price}<span className="text-sm text-gray-500">{p.per}</span>
                            </div>
                            <ul className="text-gray-400 text-xs space-y-2 mb-6">
                                {p.bullets.map(b => (
                                    <li key={b} className="border-t border-gray-800 pt-2 first:border-t-0 first:pt-0">{b}</li>
                                ))}
                            </ul>
                            <button
                                onClick={onStartPool}
                                className={
                                    'w-full text-sm font-bold uppercase tracking-wide py-2.5 rounded-lg ' +
                                    (p.featured
                                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                                        : 'border border-gray-700 text-gray-200 hover:border-gray-500')
                                }
                            >
                                {p.id === 'eval' ? 'Try It Free' : `Start ${p.name}`}
                            </button>
                            {p.fineprint && (
                                <p className="text-center text-gray-500 text-[11px] mt-3">{p.fineprint}</p>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* FOOTER CTA */}
            <section className="text-center py-24 px-6">
                <h2 className="text-3xl md:text-4xl font-black mb-7">Your bracket is waiting. Fill it in.</h2>
                <button
                    onClick={onStartPool}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-lg"
                >
                    Start Your Pool — Free
                </button>
            </section>

            <footer className="border-t border-gray-800 py-7 text-center text-gray-600 text-xs">
                &copy; HebertHoops. Run your own bracket pool.
            </footer>
        </div>
    );
}

export default LandingPage;
